/**
 * Required on every business (lot/deal-scoped) analytics event.
 * Prefer lot_id; include deal_id when available. deal_id alone only when no lot context exists.
 */
export type LotOrDeal = { lot_id: string; deal_id?: string } | { deal_id: string; lot_id?: string };

/** Build LotOrDeal with lot_id preferred. Returns null if neither id is present. */
export function lotOrDeal(ids: { lotId?: string | null; dealId?: string | null }): LotOrDeal | null {
  if (ids.lotId) {
    return ids.dealId ? { lot_id: ids.lotId, deal_id: ids.dealId } : { lot_id: ids.lotId };
  }
  if (ids.dealId) {
    return { deal_id: ids.dealId };
  }
  return null;
}

/** When dealId is known (method arg), produce LotOrDeal; attach lot_id when available. */
export function withDeal(dealId: string, lotId?: string | null): LotOrDeal {
  return lotId ? { lot_id: lotId, deal_id: dealId } : { deal_id: dealId };
}

/** When lotId is known, produce LotOrDeal; attach deal_id when available. */
export function withLot(lotId: string, dealId?: string | null): { lot_id: string; deal_id?: string } {
  return dealId ? { lot_id: lotId, deal_id: dealId } : { lot_id: lotId };
}

export const AnalyticsEvent = {
  AddendumCreated: 'addendum_created',
  AddendumApprovalToggled: 'addendum_approval_toggled',
  AddendumDeleted: 'addendum_deleted',
  AddendumCancelled: 'addendum_cancelled',
  AddendumOptionsAdded: 'addendum_options_added',
  AddendumOptionCancelled: 'addendum_option_cancelled',
  AddendumOptionDeleted: 'addendum_option_deleted',
  AddendumOptionDiscountUpdated: 'addendum_option_discount_updated',
  AddendumOptionIncentiveUpdated: 'addendum_option_incentive_updated',

  DealCreated: 'deal_created',
  DealReserved: 'deal_reserved',
  DealCancelled: 'deal_cancelled',
  DealContractReadyMarked: 'deal_contract_ready_marked',
  DealContractReadyReverted: 'deal_contract_ready_reverted',
  DealContractApproved: 'deal_contract_approved',
  DealMarkedApproved: 'deal_marked_approved',
  DealApprovalWithdrawn: 'deal_approval_withdrawn',
  DealMarkedUnderContract: 'deal_marked_under_contract',
  DealClosingDatesUpdated: 'deal_closing_dates_updated',
  DealClosed: 'deal_closed',
  DealBuyerReassigned: 'deal_buyer_reassigned',

  DealLotInfoUpdated: 'deal_lot_info_updated',
  DealFloorAgentUpdated: 'deal_floor_agent_updated',
  DealPricingTypeUpdated: 'deal_pricing_type_updated',
  DealCflStatusToggled: 'deal_cfl_status_toggled',

  DepositCreated: 'deposit_created',
  DepositUpdated: 'deposit_updated',
  DepositDeleted: 'deposit_deleted',
  DepositPaymentRecorded: 'deposit_payment_recorded',
  DepositPaymentUpdated: 'deposit_payment_updated',
  DepositPaymentDeleted: 'deposit_payment_deleted',

  DealContractGenerated: 'deal_contract_generated',
  DealContractRegenerated: 'deal_contract_regenerated',
  DealContractVoided: 'deal_contract_voided',
  AmendmentDocumentGenerated: 'amendment_document_generated',
  ExternalSignedDocumentUploaded: 'external_signed_document_uploaded',
  ExternalSignedDocumentReplaced: 'external_signed_document_replaced',
  DocumentSentToExecutive: 'document_sent_to_executive',
  DocusignSigningStarted: 'docusign_signing_started',

  CriticalDateCreated: 'critical_date_created',
  CriticalDateUpdated: 'critical_date_updated',
  CriticalDateCleared: 'critical_date_cleared',
  CriticalDatesDeleted: 'critical_dates_deleted',

  LoginSucceeded: 'login_succeeded',
  LoginFailed: 'login_failed',
  Logout: 'logout',
  SessionExpired: 'session_expired'
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

export interface AnalyticsEventProps {
  [AnalyticsEvent.AddendumCreated]: LotOrDeal;
  [AnalyticsEvent.AddendumApprovalToggled]: LotOrDeal & {
    addendum_id: string;
    approval_type: 'buyer' | 'builder';
    approved: boolean;
  };
  [AnalyticsEvent.AddendumDeleted]: LotOrDeal & { addendum_id: string };
  [AnalyticsEvent.AddendumCancelled]: LotOrDeal & { addendum_id: string; cancel_reason?: string };
  [AnalyticsEvent.AddendumOptionsAdded]: LotOrDeal & { addendum_id: string; option_count?: number };
  [AnalyticsEvent.AddendumOptionCancelled]: LotOrDeal & {
    addendum_option_id: string;
    cancel_reason?: string;
  };
  [AnalyticsEvent.AddendumOptionDeleted]: LotOrDeal & { addendum_option_id: string };
  [AnalyticsEvent.AddendumOptionDiscountUpdated]: LotOrDeal & {
    addendum_option_id: string;
    discount_type_id: string;
    discount_value: number;
  };
  [AnalyticsEvent.AddendumOptionIncentiveUpdated]: LotOrDeal & {
    addendum_option_id: string;
    incentive_value: number;
  };

  [AnalyticsEvent.DealCreated]: { lot_id: string; deal_id?: string };
  [AnalyticsEvent.DealReserved]: LotOrDeal;
  [AnalyticsEvent.DealCancelled]: LotOrDeal & { cancel_reason?: string };
  [AnalyticsEvent.DealContractReadyMarked]: LotOrDeal;
  [AnalyticsEvent.DealContractReadyReverted]: LotOrDeal;
  [AnalyticsEvent.DealContractApproved]: LotOrDeal;
  [AnalyticsEvent.DealMarkedApproved]: LotOrDeal;
  [AnalyticsEvent.DealApprovalWithdrawn]: LotOrDeal;
  [AnalyticsEvent.DealMarkedUnderContract]: LotOrDeal;
  [AnalyticsEvent.DealClosingDatesUpdated]: LotOrDeal & { contract_id?: string };
  [AnalyticsEvent.DealClosed]: LotOrDeal & { closing_date: string };
  [AnalyticsEvent.DealBuyerReassigned]: LotOrDeal & { buyer_id: string };

  [AnalyticsEvent.DealLotInfoUpdated]: {
    /** Lot the deal was on before this save (falls back to new_lot_id if unknown). */
    lot_id: string;
    deal_id?: string;
    /** Lot the deal is on after this save. */
    new_lot_id: string;
    model_id: string;
    elevation_id: string;
  };
  [AnalyticsEvent.DealFloorAgentUpdated]: LotOrDeal & { floor_agent_user_id: string };
  [AnalyticsEvent.DealPricingTypeUpdated]: LotOrDeal & { pricing_type: string | null };
  [AnalyticsEvent.DealCflStatusToggled]: LotOrDeal;

  [AnalyticsEvent.DepositCreated]: LotOrDeal & { deposit_count?: number };
  [AnalyticsEvent.DepositUpdated]: LotOrDeal & { deposit_id: string };
  [AnalyticsEvent.DepositDeleted]: LotOrDeal & { deposit_id: string };
  [AnalyticsEvent.DepositPaymentRecorded]: LotOrDeal & { deposit_id: string; payment_amount?: number };
  [AnalyticsEvent.DepositPaymentUpdated]: LotOrDeal & { deposit_id: string; payment_id: string };
  [AnalyticsEvent.DepositPaymentDeleted]: LotOrDeal & { payment_id: string };

  [AnalyticsEvent.DealContractGenerated]: LotOrDeal;
  [AnalyticsEvent.DealContractRegenerated]: LotOrDeal;
  [AnalyticsEvent.DealContractVoided]: LotOrDeal & { void_reason?: string };
  [AnalyticsEvent.AmendmentDocumentGenerated]: LotOrDeal;
  [AnalyticsEvent.ExternalSignedDocumentUploaded]: LotOrDeal;
  [AnalyticsEvent.ExternalSignedDocumentReplaced]: LotOrDeal;
  [AnalyticsEvent.DocumentSentToExecutive]: LotOrDeal & { deal_document_id: string };
  [AnalyticsEvent.DocusignSigningStarted]: LotOrDeal & {
    deal_document_id: string;
    signer_id: string;
    signer_name?: string;
  };

  [AnalyticsEvent.CriticalDateCreated]: LotOrDeal;
  [AnalyticsEvent.CriticalDateUpdated]: LotOrDeal & { section?: string };
  [AnalyticsEvent.CriticalDateCleared]: LotOrDeal & { section?: string };
  [AnalyticsEvent.CriticalDatesDeleted]: LotOrDeal;

  // Auth is not lot/deal-scoped
  [AnalyticsEvent.LoginSucceeded]: { user_id?: string; email?: string };
  [AnalyticsEvent.LoginFailed]: { email?: string; reason?: string };
  [AnalyticsEvent.Logout]: { user_id?: string };
  [AnalyticsEvent.SessionExpired]: Record<string, never>;
}
